import { useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { Button } from '../../../../../components/Button'
import { DialogModal } from '../../../../../components/DialogModal'
import { SearchInput } from '../../../../../components/SearchInput'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../../../constants/toast'
import { useAuth } from '../../../../../contexts/AuthContext'
import { Medias } from '../../../../../enumns/medias'
import { useMediaQuery } from '../../../../../hooks/useMediaQuery'
import { useCreateAdminMutation } from '../../../../../services/hooks/admins/mutations/useCreateAdminMutation'
import { useEditAdminMutation } from '../../../../../services/hooks/admins/mutations/useEditAdminMutation'
import { useRemoveAdminMutation } from '../../../../../services/hooks/admins/mutations/useRemoveAdminMutation'
import { type AdminDomain } from '../../../../../types/domain/admin-domain'
import { type AdminPersistence } from '../../../../../types/persistence/admin-persistence'
import { getErrorMessage } from '../../../../../utils/getErrorMessage'
import { NavigationSelect } from '../../components/NavigationSelect'
import { TableRowActions } from '../../components/TableRowActions'
import { AdminForm } from './components/AdminForm'
import { TableWithInfiniteScroll } from './components/TableWithInfiniteScroll'
import { TableWithManuallyPagination } from './components/TableWithManuallyPagination'
import { Can } from '../../../../../guards/GuardContext'

export const Admin = () => {
  const isMinScreenWidthLG = useMediaQuery(`(min-width: ${Medias.LG}px)`)
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`)

  const { user } = useAuth()

  const queryClient = useQueryClient()
  const createAdminMutation = useCreateAdminMutation()
  const editAdminMutation = useEditAdminMutation()
  const removeAdminMutation = useRemoveAdminMutation()

  const [adminToUpdate, setAdminToUpdate] = useState<AdminDomain>()
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [filters, setFilters] = useState({
    page: 1,
    take: isMinScreenWidthMD ? 10 : 20,
    search: '',
  })

  const getAdminFromCache = useCallback(
    (id: number) => {
      const adminsFromCache: any = queryClient.getQueryData(['admins', filters])

      if (isMinScreenWidthMD) {
        return adminsFromCache?.data?.find(
          (admin: AdminDomain) => admin.id === id
        )
      } else if (isMinScreenWidthLG) {
        return adminsFromCache?.pages
          .reduce((acc: AdminDomain[], item: { data: AdminDomain[] }) => {
            return [...acc, ...item.data]
          })
          .find((admin: AdminDomain) => admin.id === id)
      }
    },
    [queryClient, isMinScreenWidthMD, filters]
  )

  const handleRemoveAdmin = useCallback(
    (id: number, successAction: () => void) => {
      removeAdminMutation.mutate(id, {
        onSuccess () {
          toast.success(
            'Admin excluído com sucesso',
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )
          queryClient.invalidateQueries(['admins', filters])
          successAction()
        },
        onError (error: unknown) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      })
    },
    [filters, queryClient, getErrorMessage]
  )

  const columns = [
    {
      field: 'id',
      headerName: 'id',
      isVisible: false,
    },
    {
      field: 'name',
      headerName: 'Nome',
      widthInPercentage: 45,
    },
    {
      field: 'email',
      headerName: 'E-mail',
      widthInPercentage: 45,
    },
    {
      field: 'actions',
      headerName: 'Ações',
      widthInPercentage: 10,
      renderCell ({ id }: { id: number }) {
        return (
          <TableRowActions
            tableContentType="Admin"
            isRemovingRegister={removeAdminMutation.isLoading}
            cannotRemove={id === user?.id}
            alertMessage="Você está prestes a excluir um Admin. Deseja continuar?"
            onEdit={() => {
              const adminFromCache = getAdminFromCache(id)
              setAdminToUpdate(adminFromCache)
              setIsFormModalOpen(true)
            }}
            onRemove={(successAction) => handleRemoveAdmin(id, successAction)}
          />
        )
      },
    },
  ]

  const handleSearchAdmin = (searchTerm: string) => {
    setFilters((prevState) => ({
      ...prevState,
      page: 1,
      search: searchTerm,
    }))
  }

  const handleCreateButtonClick = () => {
    setIsFormModalOpen(true)
  }

  const handleCloseAdminFormModal = () => {
    if (adminToUpdate) {
      setAdminToUpdate(undefined)
    }

    setIsFormModalOpen(false)
  }

  const handleSaveAdmin = (data: AdminPersistence) => {
    if (adminToUpdate) {
      editAdminMutation.mutate(
        { id: adminToUpdate.id, data },
        {
          onSuccess () {
            toast.success(
              'Admin editado com sucesso',
              TOAST_SUCCESS_DEFAULT_OPTIONS
            )
            setIsFormModalOpen(false)
            setAdminToUpdate(undefined)
            queryClient.invalidateQueries(['admins', filters])
          },
          onError (error) {
            toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
          },
        }
      )
      return
    }

    createAdminMutation.mutate(data, {
      onSuccess () {
        toast.success(
          'Admin cadastrado com sucesso',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )
        setIsFormModalOpen(false)
        queryClient.invalidateQueries(['admins', filters])
      },
      onError (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  return (
    <>
      <div>
        {!isMinScreenWidthLG && <NavigationSelect defaultRoute="admin" />}

        <div className="md:flex md:justify-between md:items-center md:mb-6">
          <h1 className="mb-4 font-medium text-grey-900 md:text-lg md:mb-0">
            Administradores
          </h1>

          <div className="mb-4 md:flex md:gap-2">
            <SearchInput
              onSearch={handleSearchAdmin}
              placeholder="Buscar por nome ou E-mail"
              className="mb-6 md:w-96 md:mb-0"
            />

            <Can I="create" a="Admin">
              <Button
                className="h-10 ml-auto"
                onClick={handleCreateButtonClick}
              >
                Cadastrar
              </Button>
            </Can>
          </div>
        </div>

        {isMinScreenWidthMD ? (
          <TableWithManuallyPagination
            columns={columns}
            filters={filters}
            setFilters={setFilters}
          />
        ) : (
          <TableWithInfiniteScroll columns={columns} filters={filters} />
        )}
      </div>

      <DialogModal
        isOpen={isFormModalOpen}
        title={`${adminToUpdate ? 'Editar' : 'Cadastrar'} Administrador`}
        onClose={handleCloseAdminFormModal}
      >
        <AdminForm
          isSubmitting={
            createAdminMutation.isLoading || editAdminMutation.isLoading
          }
          defaultValues={adminToUpdate}
          onSubmit={handleSaveAdmin}
        />
      </DialogModal>
    </>
  )
}
